import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, KeyRound, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "@/components/AppShell";
import { ThemeSetting } from "@/components/ThemeToggle";
import { Button, IconButton, Spinner } from "@/components/ui";
import { Voices } from "@/components/VoiceLibrary";
import { api } from "@/lib/api";
import { formatClock } from "@/lib/format";

/** Your name, the studio's colours on this device, your password, and the keys scripts use to work as you. */
export function AccountPage() {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const keys = useQuery({ queryKey: ["api-keys"], queryFn: api.apiKeys });
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [keyName, setKeyName] = useState("");
  const [freshKey, setFreshKey] = useState<string | null>(null);

  const saveName = useMutation({
    mutationFn: (name: string) => api.updateMe(name),
    onSuccess: (updated) => {
      queryClient.setQueryData(["me"], updated);
      setDisplayName(null);
      toast.success("Name saved.");
    },
    onError: (error) => toast.error(error.message),
  });
  const changePassword = useMutation({
    mutationFn: () => api.changePassword(current, next),
    onSuccess: () => {
      setCurrent("");
      setNext("");
      toast.success("Password changed. Other devices were signed out.");
    },
    onError: (error) => toast.error(error.message),
  });
  const createKey = useMutation({
    mutationFn: () => api.createApiKey(keyName.trim()),
    onSuccess: async (created) => {
      setFreshKey(created.secret);
      setKeyName("");
      await keys.refetch();
    },
    onError: (error) => toast.error(error.message),
  });
  const revokeKey = useMutation({
    mutationFn: (id: string) => api.revokeApiKey(id),
    onSuccess: () => keys.refetch(),
    onError: (error) => toast.error(error.message),
  });

  const user = me.data?.user;
  const field = "h-11 w-full rounded-[var(--radius-control)] border border-line bg-panel px-3 text-[16px] outline-none focus-visible:border-mute";

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="Account" />
      <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto">
        <div className="mx-auto flex w-full max-w-[720px] flex-col gap-8 px-4 pb-10 pt-4 md:px-6">
          {!user ? (
            <Spinner />
          ) : (
            <>
              <section className="flex flex-col gap-3">
                <h2 className="text-[17px] font-semibold">You</h2>
                <p className="text-[14px] text-mute">
                  Signed in as <span className="text-paper">{user.username}</span>
                  {user.role === "admin" ? " · administrator" : ""}
                </p>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[13px] font-medium text-mute">Name other people see</span>
                  <div className="flex gap-2">
                    <input
                      value={displayName ?? user.displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      className={field}
                    />
                    <Button
                      variant="outline"
                      className="h-11 shrink-0"
                      disabled={displayName === null || !displayName.trim() || saveName.isPending}
                      onClick={() => displayName && saveName.mutate(displayName.trim())}
                    >
                      Save
                    </Button>
                  </div>
                </label>
              </section>

              <Voices />

              <section className="flex flex-col gap-3">
                <h2 className="text-[17px] font-semibold">Colours</h2>
                <p className="text-[14px] text-mute">Day, night, or whatever this phone or computer uses. Saved on this device.</p>
                <ThemeSetting />
              </section>

              <section className="flex flex-col gap-3">
                <h2 className="text-[17px] font-semibold">Password</h2>
                {user.mustChangePassword ? (
                  <p className="rounded-[var(--radius-control)] bg-film/15 px-3 py-2 text-[14px] text-paper">
                    You are using a temporary password. Please set your own.
                  </p>
                ) : null}
                <div className="grid gap-2 md:grid-cols-2">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-[13px] font-medium text-mute">Current password</span>
                    <input type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} className={field} />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-[13px] font-medium text-mute">New password</span>
                    <input type="password" autoComplete="new-password" value={next} onChange={(e) => setNext(e.target.value)} className={field} />
                  </label>
                </div>
                <div>
                  <Button variant="outline" disabled={!current || next.length < 8 || changePassword.isPending} onClick={() => changePassword.mutate()}>
                    Change password
                  </Button>
                </div>
              </section>

              <section className="flex flex-col gap-3">
                <h2 className="text-[17px] font-semibold">Keys for scripts</h2>
                <p className="max-w-[62ch] text-[14px] text-mute">
                  A key lets a script make things as you: send it as <span className="tabular">Authorization: Bearer …</span>. Anything it
                  makes lands in your library.
                </p>
                {freshKey ? (
                  <div className="flex flex-col gap-2 rounded-[var(--radius-control)] border border-film/50 bg-film/10 p-3">
                    <p className="text-[13px] font-medium">Copy it now — it is not shown again.</p>
                    <div className="flex items-center gap-2">
                      <code className="tabular min-w-0 flex-1 truncate rounded-[8px] bg-desk px-2 py-1.5 text-[13px]">{freshKey}</code>
                      <IconButton
                        label="Copy key"
                        onClick={() => {
                          void navigator.clipboard.writeText(freshKey);
                          toast.success("Key copied.");
                        }}
                      >
                        <Copy className="size-5" />
                      </IconButton>
                      <Button size="sm" variant="quiet" onClick={() => setFreshKey(null)}>
                        Done
                      </Button>
                    </div>
                  </div>
                ) : null}
                <div className="flex gap-2">
                  <input
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    placeholder="What is it for? e.g. Shorts batch"
                    className={field}
                  />
                  <Button variant="outline" className="h-11 shrink-0" disabled={!keyName.trim() || createKey.isPending} onClick={() => createKey.mutate()}>
                    <KeyRound className="size-4" aria-hidden />
                    New key
                  </Button>
                </div>
                <ul className="flex flex-col divide-y divide-line/60 rounded-[var(--radius-control)] border border-line">
                  {keys.data?.length ? (
                    keys.data.map((key) => (
                      <li key={key.id} className="flex items-center gap-3 px-3 py-2.5">
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-[15px]">{key.name}</p>
                          <p className="tabular text-[13px] text-mute">
                            {key.prefix}… · made {formatClock(key.createdAt)}
                            {key.lastUsedAt ? ` · used ${formatClock(key.lastUsedAt)}` : " · never used"}
                          </p>
                        </div>
                        <IconButton label="Revoke key" onClick={() => revokeKey.mutate(key.id)}>
                          <Trash2 className="size-5" />
                        </IconButton>
                      </li>
                    ))
                  ) : (
                    <li className="px-3 py-3 text-[14px] text-mute">No keys yet.</li>
                  )}
                </ul>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
